import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { radius } from "src/design/tokens";
import { fontStretch } from "src/design/typography";
import {
  AppSwitch,
  FilterPills,
  InlineAlert,
  LoadingButton,
} from "src/grading/components";
import { HeroDecoration } from "src/grading/components/HeroBand/HeroBand";
import { usePermissions } from "src/grading/hooks/usePermissions";
import PageHeader from "src/grading/layout/PageHeader";
import {
  healthFlagLabel,
  modificationLabel,
} from "src/grading/utils/accommodationLabels";
import { permissions } from "src/grading/utils/permissionsHelpers";
import {
  CATEGORY_EVENTS,
  Category,
  EVENT_LABEL,
  EventId,
  SessionWindow,
} from "src/models/sessions";
import { useAppDispatch } from "src/store/hooks";
import {
  useGetClassQuery,
  useGetMyClassesQuery,
  useGetRosterQuery,
} from "src/store/reducers/classes/classesApiSlice";
import {
  addBreadcrumb,
  clearBreadcrumbs,
} from "src/store/reducers/navigation/navigationSlice";
import { useCreateSessionMutation } from "src/store/reducers/sessions/sessionsApiSlice";
import EventIcon from "./planner/EventIcon";
import OptionCard from "./planner/OptionCard";
import StepCard from "./planner/StepCard";

/** Equipment ids; labels are `sessions.equipment.<id>`. */
const EQUIPMENT_LABEL: Record<string, string> = {
  mats: "Mats",
  stopwatch: "Stopwatch",
  partner: "Partner per student",
  stopwatchGroup: "Stopwatch per group",
  course: "Measured course or lap count",
  cones: "Cones",
  water: "Water",
  lane20: "20 m lane with cones",
  speaker20: "Speaker and the beep audio",
  lapTally: "Lap tally",
  lane15: "15 m lane with cones",
  speaker15: "Speaker and the 15 m audio",
  bar: "Pull-up bar",
  spotter: "Spotter",
  step: "Step or box",
};

const EQUIPMENT: Record<EventId, string[]> = {
  "pft-situps": ["mats", "stopwatch", "partner"],
  "pft-plank": ["mats", "stopwatchGroup"],
  "pft-mile": ["course", "stopwatch", "cones", "water"],
  "pft-beep-20m": ["lane20", "speaker20", "lapTally"],
  "pft-beep-15m": ["lane15", "speaker15"],
  "pft-pushups": ["mats", "stopwatch", "partner"],
  "pft-pullups": ["bar", "spotter", "step"],
};

const EVENT_SUB: Record<EventId, string> = {
  "pft-situps": "1 minute · correct reps",
  "pft-plank": "Until form breaks · seconds",
  "pft-mile": "Run, walk or wheel · time",
  "pft-beep-20m": "Audio-paced · laps",
  "pft-beep-15m": "Audio-paced · laps",
  "pft-pushups": "1 minute · correct reps",
  "pft-pullups": "Untimed · correct reps",
};

const WINDOWS: SessionWindow[] = ["fall", "spring", "custom"];

const overlineSx = {
  color: "text.secondary",
  fontWeight: 800,
  lineHeight: 1.4,
} as const;

/** Plan a session from a class: date, window, one event per category or "students choose", cardio last. */
const SessionPlannerPage = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const query = new URLSearchParams(useLocation().search);
  const arrivedClassId = query.get("classId") ?? "";
  // Teachers pick from the classes they teach. Admins don't teach classes (no `classes:own`), so they
  // plan for the class they arrived from (?classId=).
  const { hasPermissions } = usePermissions();
  const teachesClasses = hasPermissions(
    permissions.READ_OWN_CLASSES.action,
    permissions.READ_OWN_CLASSES.subject,
  );
  const mine = useGetMyClassesQuery(undefined, { skip: !teachesClasses });
  const arrived = useGetClassQuery(arrivedClassId, {
    skip: teachesClasses || !arrivedClassId,
  });
  const classes = teachesClasses
    ? mine.data
    : arrived.data
      ? [arrived.data]
      : arrivedClassId
        ? undefined
        : [];
  const classesError = teachesClasses ? mine.isError : arrived.isError;
  const [createSession, { isLoading }] = useCreateSessionMutation();
  const [classId, setClassId] = useState(arrivedClassId);
  const { data: roster } = useGetRosterQuery(classId, { skip: !classId });
  const flagged = (roster ?? []).filter(
    (s) =>
      (s.accommodation?.healthFlags?.length ?? 0) > 0 ||
      (s.accommodation && s.accommodation.plannedModification !== "none"),
  );
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [testWindow, setWindow] = useState<SessionWindow>(
    new Date().getMonth() >= 6 ? "fall" : "spring",
  );
  const [events, setEvents] = useState<
    Record<Category, EventId | "student-choice">
  >({ core: "pft-situps", cardio: "pft-mile", upper: "pft-pushups" });
  const [cardioLast, setCardioLast] = useState(true);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<string | null>(null);

  useEffect(() => {
    dispatch(clearBreadcrumbs());
    dispatch(
      addBreadcrumb({ label: t("tabs.sessions"), path: "/grading/sessions" }),
    );
    dispatch(addBreadcrumb({ label: t("sessions.plan") }));
    return () => {
      dispatch(clearBreadcrumbs());
    };
  }, [dispatch, t]);

  const chosen = useMemo(
    () =>
      (
        Object.entries(events) as [Category, EventId | "student-choice"][]
      ).flatMap(([c, e]) =>
        e === "student-choice" ? CATEGORY_EVENTS[c] : [e],
      ),
    [events],
  );
  const equipment = Array.from(new Set(chosen.flatMap((e) => EQUIPMENT[e])));

  const submit = async (makeUp = false) => {
    setError(null);
    try {
      const { session, warnings } = await createSession({
        classId,
        date,
        window: testWindow,
        eventsByCategory: events,
        cardioLast,
        name: name.trim() || undefined,
        makeUp,
      }).unwrap();
      if (warnings.includes("duplicate_window") && !makeUp) {
        setDuplicate(session.id);
        return;
      }
      history.push(`/grading/sessions/${session.id}`);
    } catch (err: unknown) {
      const data = (err as { data?: { message?: string | string[] } } | null)
        ?.data;
      const message = Array.isArray(data?.message)
        ? data?.message.join(", ")
        : data?.message;
      setError(
        message ||
          t("sessions.planError", {
            defaultValue: "Could not plan the session.",
          }),
      );
    }
  };

  /** "Thursday, October 8" from the planner's "YYYY-MM-DD" date, read as a calendar date. */
  const longDate = (() => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    if (!match) return date;
    return new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
    ).toLocaleDateString(i18n.language, {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
  })();
  const eventLabel = (e: EventId) =>
    t(`events.${e}`, { defaultValue: EVENT_LABEL[e] });
  const categories = Object.keys(CATEGORY_EVENTS) as Category[];
  const selectedClass = (classes ?? []).find((c) => c.id === classId);
  const studentCount = roster?.length ?? 0;
  const studentsLabel = (count: number) =>
    t("sessions.studentsCount", {
      count,
      defaultValue: "{{count}} students",
    });

  return (
    <Box>
      <PageHeader
        title={t("sessions.plan")}
        description={t("sessions.plannerDescription", {
          defaultValue:
            "Pick the class, the day and the events. We'll set up the stations, the prep checklist and the station cards.",
        })}
      />
      <Box
        sx={(theme) => ({
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr)",
          gap: 3,
          alignItems: "start",
          [theme.breakpoints.up(1024)]: {
            gridTemplateColumns: "minmax(0, 1fr) 340px",
          },
        })}
      >
        <Stack spacing={2.25} sx={{ minWidth: 0 }}>
          {duplicate && (
            <InlineAlert
              severity="warning"
              title={t("sessions.duplicateWindow")}
              action={
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    color="inherit"
                    onClick={() =>
                      history.push(`/grading/sessions/${duplicate}`)
                    }
                  >
                    {t("sessions.keepRegular", {
                      defaultValue: "Keep as regular",
                    })}
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() =>
                      history.push(`/grading/sessions/${duplicate}`)
                    }
                  >
                    {t("sessions.makeUp")}
                  </Button>
                </Stack>
              }
            >
              {t("sessions.duplicateCreated", {
                defaultValue:
                  "The session was created. Mark it as a make-up so reports count it separately, or keep it as a regular session.",
              })}
            </InlineAlert>
          )}

          <StepCard
            step={1}
            title={t("sessions.step1", { defaultValue: "Class and day" })}
          >
            <Box
              role="group"
              aria-label={t("sessions.class")}
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                gap: 1.25,
              }}
            >
              {/* Only classes the caller teaches; admins arrive with ?classId= from the class page. */}
              {(classesError || (classes?.length === 0 && !classId)) && (
                <Box sx={{ gridColumn: "1 / -1" }}>
                  {classesError ? (
                    <InlineAlert severity="error">
                      {t("classes.loadError", {
                        defaultValue:
                          "Could not load. Check your connection and try again.",
                      })}
                    </InlineAlert>
                  ) : (
                    <InlineAlert
                      severity="info"
                      title={t("sessions.noClasses", {
                        defaultValue: "No classes assigned yet.",
                      })}
                    >
                      {t("sessions.noClassesBody", {
                        defaultValue:
                          "The planner lists the classes you teach. To plan for another class, open the class from its school page and choose Plan a session.",
                      })}
                    </InlineAlert>
                  )}
                </Box>
              )}
              {(classes ?? []).map((c) => {
                const selected = c.id === classId;
                const sub = [
                  c.gradeLevel ? `${t("classes.grade")} ${c.gradeLevel}` : null,
                  c.period ? `Period ${c.period}` : null,
                  selected && roster ? studentsLabel(studentCount) : null,
                ]
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <OptionCard
                    key={c.id}
                    label={c.name}
                    sub={sub || undefined}
                    selected={selected}
                    onSelect={() => setClassId(c.id)}
                  />
                );
              })}
            </Box>
            <TextField
              label={t("sessions.nameOptional")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              inputProps={{ maxLength: 120 }}
              fullWidth
            />
            <Stack
              direction="row"
              spacing={2}
              alignItems="flex-end"
              flexWrap="wrap"
              useFlexGap
            >
              <TextField
                label={t("sessions.testDate")}
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
              <Stack spacing={0.75}>
                <Typography
                  component="span"
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "text.secondary",
                  }}
                >
                  {t("sessions.windowLabel")}
                </Typography>
                <FilterPills
                  variant="trayAlt"
                  aria-label={t("sessions.windowLabel")}
                  value={testWindow}
                  onChange={(next) => setWindow(next as SessionWindow)}
                  options={WINDOWS.map((w) => ({
                    value: w,
                    label: t(`sessions.window.${w}`),
                  }))}
                />
              </Stack>
              <Typography
                sx={{
                  fontSize: 13,
                  lineHeight: 1.4,
                  color: "text.secondary",
                  maxWidth: 280,
                }}
              >
                {t("sessions.twiceAYear", {
                  defaultValue:
                    "The Council recommends testing twice a year, 8 to 12 weeks apart.",
                })}
              </Typography>
            </Stack>
          </StepCard>

          <StepCard
            step={2}
            title={t("sessions.step2", {
              defaultValue: "One event per category",
            })}
          >
            {categories.map((category) => (
              <Box
                key={category}
                role="group"
                aria-labelledby={`${category}-label`}
                sx={(theme) => ({
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr)",
                  gap: 1.25,
                  [theme.breakpoints.up("sm")]: {
                    gridTemplateColumns: "110px minmax(0, 1fr)",
                    gap: 1.75,
                  },
                })}
              >
                <Typography
                  id={`${category}-label`}
                  variant="overline"
                  component="p"
                  sx={{ color: "dangerInk", lineHeight: 1, pt: { sm: 2 } }}
                >
                  {t(`categories.${category}`)}
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(170px, 1fr))",
                    gap: 1.25,
                  }}
                >
                  {CATEGORY_EVENTS[category].map((e) => (
                    <OptionCard
                      key={e}
                      radio
                      label={eventLabel(e)}
                      sub={t(`events.sub.${e}`, { defaultValue: EVENT_SUB[e] })}
                      icon={<EventIcon eventId={e} />}
                      selected={events[category] === e}
                      onSelect={() => setEvents({ ...events, [category]: e })}
                    />
                  ))}
                  <OptionCard
                    radio
                    label={t("sessions.studentsChoose")}
                    sub={t("sessions.studentsChooseSub", {
                      a: eventLabel(CATEGORY_EVENTS[category][0]),
                      b: eventLabel(CATEGORY_EVENTS[category][1]),
                      defaultValue: "{{a}} or {{b}}",
                    })}
                    selected={events[category] === "student-choice"}
                    onSelect={() =>
                      setEvents({ ...events, [category]: "student-choice" })
                    }
                  />
                </Box>
              </Box>
            ))}
            <Box
              sx={{
                bgcolor: "surfaceAlt",
                borderRadius: `${radius.panel}px`,
                px: 2,
                py: 1,
              }}
            >
              <AppSwitch
                checked={cardioLast}
                onChange={(e) => setCardioLast(e.target.checked)}
                label={
                  <Stack spacing={0.25}>
                    <Typography
                      component="span"
                      sx={{ fontSize: 15, fontWeight: 800 }}
                    >
                      {t("sessions.cardioLast")}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{ fontSize: 13, color: "text.secondary" }}
                    >
                      {t("sessions.cardioLastNote", {
                        defaultValue:
                          "The cardio station stays locked until the strength stations finish, as the large-class guide recommends.",
                      })}
                    </Typography>
                  </Stack>
                }
              />
            </Box>
          </StepCard>

          {classId && (
            <StepCard
              step={3}
              title={t("sessions.beforeOpen")}
              aside={t("sessions.summaryFrom", {
                defaultValue: "From IEP/504 plans and health notes",
              })}
            >
              {flagged.length === 0 ? (
                <Typography color="text.secondary">
                  {t("sessions.noFlags")}
                </Typography>
              ) : (
                <Stack component="ul" spacing={1} sx={{ m: 0, p: 0 }}>
                  {flagged.map((s) => {
                    const health = !!s.accommodation?.healthFlags?.length;
                    const planned =
                      !!s.accommodation &&
                      s.accommodation.plannedModification !== "none";
                    return (
                      <Stack
                        key={s.id}
                        component="li"
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                        sx={{
                          listStyle: "none",
                          px: 1.75,
                          py: 1.5,
                          borderRadius: `${radius.control}px`,
                          bgcolor: health ? "dangerSoft" : "warning.light",
                        }}
                      >
                        <Chip
                          size="small"
                          label={
                            health
                              ? t("sessions.flagTag.health", {
                                  defaultValue: "Health",
                                })
                              : t("sessions.flagTag.planned", {
                                  defaultValue: "Planned",
                                })
                          }
                          sx={{
                            bgcolor: "background.paper",
                            color: health ? "dangerInk" : "warning.dark",
                            fontSize: 11,
                            fontWeight: 800,
                            textTransform: "uppercase",
                          }}
                        />
                        <Typography
                          component="span"
                          sx={{ fontSize: 14, fontWeight: 800 }}
                        >
                          {s.lastName}, {s.firstName}
                        </Typography>
                        <Typography
                          component="span"
                          sx={{
                            fontSize: 14,
                            color: health ? "dangerInk" : "warning.dark",
                          }}
                        >
                          {[
                            health
                              ? s.accommodation?.healthFlags
                                  .map((f) => healthFlagLabel(t, f))
                                  .join(", ")
                              : null,
                            planned && s.accommodation
                              ? `${t("sessions.plannedModification", {
                                  defaultValue: "Planned: {{modification}}",
                                  modification: modificationLabel(
                                    t,
                                    s.accommodation.plannedModification,
                                  ),
                                })}${s.accommodation.plannedNote ? ` (${s.accommodation.plannedNote})` : ""}`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                {t("classes.accommodationAccess", {
                  defaultValue:
                    "Visible to staff with accommodation access. Volunteer scorers never see health notes.",
                })}
              </Typography>
            </StepCard>
          )}
        </Stack>

        <Paper
          component="aside"
          aria-labelledby="planner-summary"
          sx={(theme) => ({
            overflow: "hidden",
            borderRadius: `${radius.card}px`,
            display: "flex",
            flexDirection: "column",
            [theme.breakpoints.up(1024)]: { position: "sticky", top: 96 },
          })}
        >
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              p: "18px 20px",
              bgcolor: "hero",
              color: "heroInk",
            }}
          >
            <HeroDecoration bands={false} dotSize={18} />
            <Box
              sx={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                gap: 0.75,
              }}
            >
              <Typography
                id="planner-summary"
                component="h2"
                sx={{
                  m: 0,
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                  color: "heroMuted",
                }}
              >
                {t("sessions.summary", { defaultValue: "Session summary" })}
              </Typography>
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 900,
                  lineHeight: 1.05,
                  fontStretch: fontStretch.display,
                  textTransform: "uppercase",
                }}
              >
                {selectedClass?.name ?? t("sessions.class")}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "heroMuted" }}>
                {selectedClass
                  ? `${longDate} · ${studentsLabel(studentCount)}`
                  : longDate}
              </Typography>
            </Box>
          </Box>
          <Stack spacing={2} sx={{ p: "18px 20px" }}>
            <Stack spacing={1.25}>
              <Typography
                variant="overline"
                component="h3"
                sx={{ ...overlineSx, lineHeight: 1 }}
              >
                {t("sessions.stationsCount", {
                  count: chosen.length,
                  defaultValue: "{{count}} stations",
                })}
              </Typography>
              {categories.map((category) => {
                const pick = events[category];
                const lanes = pick === "student-choice" ? 2 : 1;
                return (
                  <Box
                    key={category}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1.25,
                      fontSize: 13,
                      fontWeight: 600,
                      lineHeight: 1.3,
                    }}
                  >
                    <span>
                      <Box component="b" sx={{ fontWeight: 800 }}>
                        {t(`categories.${category}`)}
                      </Box>
                      {" · "}
                      {pick === "student-choice"
                        ? CATEGORY_EVENTS[category].map(eventLabel).join(" + ")
                        : eventLabel(pick)}
                    </span>
                    <Box
                      component="span"
                      sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
                    >
                      {t("sessions.lanes", {
                        count: lanes,
                        defaultValue: "{{count}} lanes",
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
            <Stack spacing={1}>
              <Typography
                variant="overline"
                component="h3"
                sx={{ ...overlineSx, lineHeight: 1 }}
              >
                {t("sessions.prepChecklist")}
              </Typography>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  p: 0,
                  listStyle: "none",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  fontSize: 13,
                }}
              >
                {[
                  ...equipment.map((item) =>
                    t(`sessions.equipment.${item}`, {
                      defaultValue: EQUIPMENT_LABEL[item],
                    }),
                  ),
                  t("sessions.checklist.stationCards", {
                    defaultValue: "Print station cards for {{count}} stations",
                    count: chosen.length,
                  }),
                  ...(chosen.some((e) => e.startsWith("pft-beep"))
                    ? [t("sessions.checklist.audio")]
                    : []),
                  t("sessions.checklist.plans"),
                  t("sessions.checklist.volunteers"),
                ].map((item) => (
                  <Box
                    component="li"
                    key={item}
                    sx={{ display: "flex", gap: 1.25, alignItems: "center" }}
                  >
                    <Box
                      aria-hidden
                      sx={(theme) => ({
                        width: 16,
                        height: 16,
                        flex: "none",
                        borderRadius: "5px",
                        border: `1.5px solid ${theme.palette.text.disabled}`,
                      })}
                    />
                    {item}
                  </Box>
                ))}
              </Box>
              <Box>
                <Button size="small" onClick={() => window.print()}>
                  {t("sessions.printChecklist", {
                    defaultValue: "Print checklist",
                  })}
                </Button>
              </Box>
            </Stack>
            {error && <InlineAlert severity="error">{error}</InlineAlert>}
            <LoadingButton
              variant="contained"
              size="large"
              fullWidth
              label={t("sessions.planSession", {
                defaultValue: "Plan session",
              })}
              loading={isLoading}
              disabled={!classId || !date}
              aria-describedby={!classId ? "plan-session-hint" : undefined}
              onClick={() => submit()}
            />
            {!classId && (
              <Typography
                id="plan-session-hint"
                sx={{
                  fontSize: 13,
                  lineHeight: 1.4,
                  color: "text.secondary",
                  textAlign: "center",
                }}
              >
                {t("sessions.pickClassHint", {
                  defaultValue: "Pick a class in step 1 to plan the session.",
                })}
              </Typography>
            )}
            <Button
              variant="text"
              color="inherit"
              fullWidth
              onClick={() => history.push("/grading/sessions")}
            >
              {t("common.cancel")}
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
};

export default SessionPlannerPage;
